/**
 * calendar-range — pure date-range math for the PA Calendar views (PROD-1190 Slice 2).
 *
 * Maps a (view, date) pair to the DATETIME range + backend `filter` the
 * appointment query needs. Mirrors `dayRange`'s widened-bounds format
 * (T00:00:00.000 / T23:59:59.000) so date-only strings don't collapse to a
 * zero-width range on the backend.
 *
 * Local date math only (no UTC) — keeps parity with the rest of scheduling,
 * which renders/queries in the user's local day.
 *   - week  = Monday..Sunday containing `date`
 *   - month = 1st..last day of `date`'s month
 *   - day   = that single day
 */
import type { DateFilter } from '@/types/appointment-api';

export type CalendarRangeView = 'day' | 'week' | 'month';

export interface CalendarRange {
  startDate: string;
  endDate: string;
  filter: Extract<DateFilter, 'Day' | 'Week' | 'Month'>;
}

/** Parse YYYY-MM-DD into a local Date at midnight. */
function parseLocal(date: string): Date {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/** Format a local Date as YYYY-MM-DD. */
function toIso(dt: Date): string {
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/** Widen a YYYY-MM-DD start/end pair to full-day DATETIME bounds (dayRange format). */
function bounds(startIso: string, endIso: string): { startDate: string; endDate: string } {
  return {
    startDate: `${startIso}T00:00:00.000`,
    endDate: `${endIso}T23:59:59.000`,
  };
}

/**
 * The DATETIME range + backend filter for a calendar `view` anchored on `date`.
 */
export function rangeFor(view: CalendarRangeView, date: string): CalendarRange {
  const anchor = parseLocal(date);

  if (view === 'week') {
    // Monday-start week. JS getDay(): 0=Sun..6=Sat → offset back to Monday.
    const dow = anchor.getDay();
    const sinceMonday = (dow + 6) % 7; // Mon→0, Sun→6
    const monday = new Date(anchor);
    monday.setDate(anchor.getDate() - sinceMonday);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return { ...bounds(toIso(monday), toIso(sunday)), filter: 'Week' };
  }

  if (view === 'month') {
    const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const last = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
    return { ...bounds(toIso(first), toIso(last)), filter: 'Month' };
  }

  // day
  const iso = toIso(anchor);
  return { ...bounds(iso, iso), filter: 'Day' };
}
